import React, { useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { CCol, CFormCheck, CLoadingButton, CRow, CSpinner } from '@coreui/react-pro'
import { mdiCheckboxOutline } from '@mdi/js'
import Icon from '@mdi/react'

import { ReactComponent as BestPriceTag } from '@assets/images/best-price-tag.svg'
import { ReactComponent as WomanWorkingImage } from '@assets/images/woman-working.svg'
import { battleshipGrey, black, middleBlue } from '@constants/colors'
import { pricing } from '@constants/general'
import { useUserLocationInfoQuery } from '@graphQLGenerated'
import { ISubscriptionSwitch } from '@interfaces/components/atoms/SubscriptionSwitch'

import BulletPoint from '../BulletPoint'

import './styles.scss'

const SubscriptionSwitch: React.FC<ISubscriptionSwitch> = ({
  onSubmit,
  buttonText = 'Proceed to payment',
  className,
  isLoading,
  showHeader = true,
  showHubSpotIncentive,
  showButton = true,
  // defaultState = false,
  defaultIsDiscountCodeChecked = false,
  hideImage = false,
  showFreePlan = true,
  showFeatureList = true,
  showDiscountCodeCheck = false,
}) => {
  const [isYearly] = useState(false)
  const [isWithDiscount, setIsWithDiscount] = useState(defaultIsDiscountCodeChecked)

  const { data, loading } = useUserLocationInfoQuery()

  useEffect(() => {
    if (!showButton) {
      onSubmit({ isYearly: isYearly, isWithDiscountCode: isWithDiscount })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const yearlyNormalCost = pricing.monthly * 12
  // const savePercentage = 100 - Math.round((pricing.yearly / yearlyNormalCost) * 100)

  const planMetaData = useMemo(() => {
    if (isYearly) {
      return {
        price: pricing.yearly,
        periodText: 'yr',
        billedText: 'Billed yearly, cancel anytime',
      }
    }
    return {
      price: pricing.monthly,
      periodText: 'mo',
      billedText: 'Billed monthly, cancel anytime',
    }
  }, [isYearly])

  const currency = useMemo(() => {
    if (data?.userLocationInfo?.countryCode === 'GB') {
      return '£'
    }
    if (data?.userLocationInfo?.eu === true) {
      return '€'
    }
    return '$'
  }, [data?.userLocationInfo])

  const _renderFeatureList = useMemo(() => {
    if (!showFeatureList) {
      return null
    }
    return (
      <div className={isMobile ? 'caption-font' : 'body-small-font'}>
        {[
          'Automatically generate and copy progress notes',
          'No integration required - just copy and paste',
          'Create and send custom assignments to your clients',
          'Secure, encrypted, HIPAA and GDPR compliant',
          'Bespoke support from our team, 24/7',
        ].map((feature) => (
          <BulletPoint
            key={feature}
            text={feature}
            bulletPointText={
              <Icon path={mdiCheckboxOutline} color={showFreePlan ? middleBlue : black} size={1} />
            }
          />
        ))}
      </div>
    )
  }, [showFeatureList, showFreePlan])

  const _renderButton = useMemo(() => {
    if (!showButton) {
      return null
    }
    return (
      <CLoadingButton
        onClick={() => {
          onSubmit({ isYearly: isYearly, isWithDiscountCode: isWithDiscount })
        }}
        className="mt-3 w-100"
        loading={isLoading}
      >
        {buttonText}
      </CLoadingButton>
    )
  }, [buttonText, isLoading, isWithDiscount, isYearly, onSubmit, showButton])

  return (
    <div className={`d-flex column ${className}`}>
      {showHeader && (
        <div className="SSHeaderContainer mb-4">
          <div className="h4-font">Start your 7-days Free Trial</div>
          <div className="body-large-font">Select a plan that is right for you</div>
        </div>
      )}
      {!hideImage ? <WomanWorkingImage className="align-self-center mb-3" /> : null}
      {/* <CCol lg={showFreePlan ? 9 : 12} xs={12} className="SSSwitchContainer mb-3">
        <div
          className={`SSSwitchButton  ${!isYearly ? 'SSSelectedSwitchButton' : ''} button-font`}
          onClick={() => {
            setIsYearly(false)
            if (!showButton) {
              onSubmit({ isYearly: false, isWithDiscountCode: isWithDiscount })
            }
          }}
        >
          Pay Monthly
        </div>
        <div
          className={`SSSwitchButton  ${isYearly ? 'SSSelectedSwitchButton' : ''} button-font`}
          onClick={() => {
            setIsYearly(true)
            if (!showButton) {
              onSubmit({ isYearly: true, isWithDiscountCode: isWithDiscount })
            }
          }}
        >
          <div>Pay Yearly</div>
          <div className="SSSaveTextContainer overline-font">save {savePercentage}%</div>
        </div>
      </CCol> */}
      <CRow sm={{ gutter: 2 }} xs={{ gutter: 3 }}>
        {showFreePlan && (
          <CCol sm={6} xs={12}>
            <div className="SSSubscriptionInfoContainer">
              <div className="body-large-font">
                <span className="SSPlanTypeText bold">Basic</span> Plan
              </div>
              {loading ? (
                <CSpinner />
              ) : (
                <div className="h5-font">
                  {currency}
                  <span className="h1-font">0</span>
                </div>
              )}
              <div className="body-small-font mb-3" style={{ color: battleshipGrey }}>
                Free to use
              </div>
              {showButton ? (
                <div className="SSCurrentPlanButton button-font mb-3">Your Current Plan</div>
              ) : null}
              <div className={isMobile ? 'caption-font' : 'body-small-font'}>
                {[
                  'Access to interactive assignments',
                  'Up to 5 session transcriptions per month',
                  'Send up to 10 assignments via email & SMS',
                  'Access visual charts of assignment results',
                  'Create custom assignments',
                  'Secure, encrypted and HIPAA compliant',
                ].map((feature) => (
                  <BulletPoint
                    key={feature}
                    text={feature}
                    bulletPointText={<Icon path={mdiCheckboxOutline} color={middleBlue} size={1} />}
                  />
                ))}
              </div>
            </div>
          </CCol>
        )}
        <CCol sm={showFreePlan ? 6 : 12} xs={12}>
          <div
            className={`${
              showFreePlan ? 'SSSubscriptionInfoContainer' : 'SSSubscriptionInfoContainerPaidTrial'
            } mb-1`}
          >
            {showFreePlan && (
              <div className="body-large-font">
                <span className="SSPlanTypeText bold">Pro</span> Plan
              </div>
            )}
            {isYearly && (
              <div className="SSBestPriceContainer">
                <BestPriceTag />
                <div className="SSBestPriceText body-large-bold-font">Best Deal</div>
              </div>
            )}
            {loading ? (
              <CSpinner className="align-self-center mb-2" />
            ) : (
              <div className="h5-font">
                {currency}
                {isYearly && <span className="line-through">{yearlyNormalCost}</span>}
                <span className="h3-font">{planMetaData.price}</span>/{planMetaData.periodText}
              </div>
            )}
            <div className="body-small-font" style={{ color: battleshipGrey }}>
              {planMetaData.billedText}
            </div>
            {showFreePlan && _renderFeatureList}
            {showFreePlan && _renderButton}
          </div>
          {showDiscountCodeCheck && (
            <>
              <CFormCheck
                id="discountCode"
                name="discountCode"
                label={
                  <div className="body-large">
                    <span className="bold">I have a discount code</span> (applied at checkout)
                  </div>
                }
                checked={isWithDiscount}
                onChange={() => {
                  setIsWithDiscount((prev) => {
                    if (!showButton) {
                      onSubmit({ isYearly: isYearly, isWithDiscountCode: !prev })
                    }
                    return !prev
                  })
                }}
              />
            </>
          )}
          {!showFreePlan && <div className="mt-3">{_renderFeatureList}</div>}
          {!showFreePlan && _renderButton}
        </CCol>
      </CRow>
      {showHubSpotIncentive ? (
        <div className="body-small-font align-self-center mt-2">
          We’ll re-direct you to Hubspot for a secure payment.
        </div>
      ) : null}
    </div>
  )
}

export default SubscriptionSwitch
